import React, { useRef, useState } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

// Tap-to-record mic button. Records a short audio clip, sends it to the given
// backend endpoint, and returns the result.
// - onResult(data): receives the full response body (used for smart multi-field fill).
// - onTranscript(text): receives just the transcript (used for single-field dictation).
// - label: when provided, renders a wide pill button with text (Smart Voice Fill).
export const VoiceInput = ({
  onTranscript,
  onResult,
  endpoint = '/public/transcribe',
  testId,
  title = 'Dictate',
  label,
}) => {
  const [status, setStatus] = useState('idle'); // idle | recording | processing
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const cleanupStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error('Voice input is not supported on this device');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        cleanupStream();
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size === 0) {
          setStatus('idle');
          return;
        }
        setStatus('processing');
        try {
          const fd = new FormData();
          fd.append('file', blob, 'audio.webm');
          const res = await api.post(endpoint, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (onResult) {
            onResult(res.data || {});
          } else {
            const text = (res.data?.text || '').trim();
            if (text) onTranscript(text);
            else toast.error("Didn't catch that — please try again");
          }
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Transcription failed');
        } finally {
          setStatus('idle');
        }
      };
      recorder.start();
      setStatus('recording');
    } catch (err) {
      toast.error('Microphone access denied');
      setStatus('idle');
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  };

  const handleClick = () => {
    if (status === 'idle') startRecording();
    else if (status === 'recording') stopRecording();
  };

  const icon =
    status === 'processing' ? (
      <Loader2 className="w-4 h-4 animate-spin" />
    ) : status === 'recording' ? (
      <Square className="w-3.5 h-3.5" />
    ) : (
      <Mic className="w-4 h-4" />
    );

  if (label) {
    const text =
      status === 'recording' ? 'Recording… tap to stop' : status === 'processing' ? 'Filling…' : label;
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={status === 'processing'}
        title={title}
        data-testid={testId}
        className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium transition-colors ${
          status === 'recording'
            ? 'bg-red-500 text-white animate-pulse'
            : status === 'processing'
            ? 'bg-slate-200 text-slate-500'
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        {icon}
        {text}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={status === 'processing'}
      title={status === 'recording' ? 'Stop & transcribe' : title}
      aria-label={title}
      data-testid={testId}
      className={`flex items-center justify-center w-10 h-10 shrink-0 rounded-full transition-colors ${
        status === 'recording'
          ? 'bg-red-500 text-white animate-pulse'
          : status === 'processing'
          ? 'bg-slate-200 text-slate-500'
          : 'bg-slate-100 text-slate-500 hover:bg-blue-100 hover:text-blue-600'
      }`}
    >
      {icon}
    </button>
  );
};

export default VoiceInput;
